import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { updateListingResourceIdAction } from "../actions";
import { useToast } from "@/hooks/use-toast";
import { USER_LISTINGS_QUERY_KEYS } from "../queries/useUserListings";
import { ROOM_LISTINGS_QUERY_KEYS } from "../queries/useRoomListing";
import { PORTFOLIO_QUERY_KEYS } from "@/components/modules/portfolio/api/queries/usePortfolio";
import { PORTFOLIOS_QUERY_KEYS } from "@/components/modules/portfolio/api/queries/usePortfolios";

export const useUpdateListingResourceId = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const t = useTranslations("modules.portfolio.associateListingToUnit.toast");

  return useMutation({
    mutationFn: ({
      listingId,
      resourceId,
    }: {
      listingId: string;
      resourceId: string | null;
      unitName?: string;
    }) => updateListingResourceIdAction({ listingId, resourceId }),
    onSuccess: (_, variables) => {
      toast({
        title: t("successTitle"),
        description: variables.unitName
          ? t("associatedDescription", { unitName: variables.unitName })
          : t("dissociatedDescription"),
      });

      // Association/dissociation changes both sides — the listing's
      // `portfolioContext` and every portfolio's unit grid (where the
      // unit gains/loses its `realEstateAssociated`).
      queryClient.invalidateQueries({
        queryKey: USER_LISTINGS_QUERY_KEYS.all,
      });
      queryClient.invalidateQueries({
        queryKey: ROOM_LISTINGS_QUERY_KEYS.getRoomListing(variables.listingId),
      });
      queryClient.invalidateQueries({
        queryKey: PORTFOLIOS_QUERY_KEYS.all,
      });
      queryClient.invalidateQueries({
        queryKey: PORTFOLIO_QUERY_KEYS.all,
      });
    },
    onError: () => {
      toast({
        title: t("errorTitle"),
        variant: "destructive",
      });
    },
  });
};
